import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  FileDownload,
  GetApp,
  Link as LinkIcon,
  OpenInNew,
  PictureAsPdf,
  Refresh,
  School,
  Visibility,
  WarningAmber,
  WorkspacePremium,
  CheckCircleOutline,
  ErrorOutline,
  Schedule,
  Update,
  MoreHoriz
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../../../contexts/ToastContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { ROLES } from '../../../../constants/roles';
import { roleMatches } from '../../../../utils/rolePermissions';
import {
  downloadCertificateVersion,
  downloadUploadedFile,
  previewCertificateVersion,
  previewUploadedFile
} from '../../../../utils/certificateTrainingFiles';
import { downloadAuthenticatedPdf, viewAuthenticatedPdf, openAuthenticatedPdfViaToken } from '../../../../utils/authenticatedPdf';
import CertificateManagement from '../../../onboarding/CertificateManagement';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const TRAINING_TYPES = [
  { value: 'REQUIRED', label: 'Required' },
  { value: 'OPTIONAL', label: 'Optional' },
  { value: 'RECURRING', label: 'Recurring' },
  { value: 'CERTIFICATION', label: 'Certification' }
];

const STATUS_COLOR = {
  EXPIRED: 'error',
  EXPIRING: 'warning',
  OK: 'success',
  NO_EXPIRY: 'default'
};

const ACCENT = {
  certificates: '#1B4F72',
  trainings: '#1A5276',
  expired: '#922B21',
  expiring: '#9A7D0A',
  ok: '#196F3D'
};

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—');

const emptyForm = {
  crewId: '',
  trainingId: '',
  trainingName: '',
  trainingType: 'REQUIRED',
  acquiredDate: dayjs().format('YYYY-MM-DD'),
  performedOnDate: '',
  expiryDate: '',
  periodicityMonths: '12',
  hasNoExpiry: false,
  reminderDates: [],
  reminderDateInput: '',
  certificateUrl: '',
  remarks: '',
  file: null
};

const emptyCertForm = {
  crewId: '',
  certificateId: '',
  onboardingId: null,
  source: 'user',
  name: '',
  type: 'OTHER',
  acquiredDate: dayjs().format('YYYY-MM-DD'),
  performedOnDate: '',
  expiryDate: '',
  periodicityMonths: '12',
  hasNoExpiry: false,
  certificateUrl: '',
  remarks: '',
  file: null
};

const CERTIFICATE_TYPES = [
  { value: 'DSC', label: 'Digital Signature Certificate' },
  { value: 'PILOT_LICENSE', label: 'Pilot License' },
  { value: 'PROFESSIONAL_LICENSE', label: 'Professional License' },
  { value: 'QUALIFICATION_CERTIFICATE', label: 'Qualification Certificate' },
  { value: 'AIRCRAFT_ENDORSEMENT', label: 'Aircraft Endorsement' },
  { value: 'OTHER', label: 'Other' }
];

const tableHeadSx = {
  bgcolor: 'grey.50',
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid',
    borderColor: 'divider',
    py: 1.25
  }
};

function MetricTile({ label, value, accent, icon }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: '1 1 140px',
        minWidth: 140,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        borderTop: `3px solid ${accent}`,
        bgcolor: 'background.paper'
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}
          >
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700, color: accent, lineHeight: 1.1 }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: accent, opacity: 0.85, mt: 0.25 }}>{icon}</Box>
      </Stack>
    </Paper>
  );
}

function StatusChip({ status, daysRemaining }) {
  const label =
    status === 'EXPIRING' && daysRemaining != null
      ? `${daysRemaining}d left`
      : status === 'EXPIRED' && daysRemaining != null
        ? `${Math.abs(daysRemaining)}d overdue`
        : status === 'NO_EXPIRY'
          ? 'No expiry'
          : status === 'OK'
            ? 'Valid'
            : status;
  return <Chip size="small" label={label} color={STATUS_COLOR[status] || 'default'} sx={{ fontWeight: 600 }} />;
}

function AlertItem({ item, showCrew, canAct, onAction, onPreview, onDownload }) {
  const overdue = item.status === 'EXPIRED';
  const canFile = Boolean(item.hasFile || item.fileUrl || item.fileName);
  const canUrl = Boolean(item.hasExternalUrl || item.certificateUrl);
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: overdue ? 'error.light' : 'warning.light',
        borderLeft: '3px solid',
        borderLeftColor: overdue ? 'error.main' : 'warning.main',
        bgcolor: 'background.paper'
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap title={item.name}>
            {item.name}
          </Typography>
          {showCrew && (
            <Typography variant="caption" color="text.secondary" display="block">
              {item.crewName}
            </Typography>
          )}
          <Stack direction="row" spacing={0.75} mt={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={item.kind === 'certificate' ? 'Certificate' : 'Training'}
              variant="outlined"
              sx={{ height: 22 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {formatDate(item.expiryDate)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" useFlexGap alignItems="center">
            {canFile && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => onPreview?.(item)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Preview
              </Button>
            )}
            {!canFile && canUrl && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<LinkIcon />}
                href={item.certificateUrl}
                target="_blank"
                component="a"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Open URL
              </Button>
            )}
            {canAct && (
              <Button
                size="small"
                variant="contained"
                startIcon={<Update />}
                onClick={() => onAction(item)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: overdue ? '#922B21' : '#9A7D0A',
                  '&:hover': { bgcolor: overdue ? '#7B241C' : '#7D6608' }
                }}
              >
                {overdue ? 'Renew / update' : 'Update before expiry'}
              </Button>
            )}
          </Stack>
        </Box>
        <Stack spacing={0.5} alignItems="flex-end">
          <StatusChip status={item.status} daysRemaining={item.daysRemaining} />
          {canFile && (
            <Tooltip title="Download certificate">
              <IconButton size="small" onClick={() => onDownload?.(item)}>
                <GetApp fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * Professional Certificates & Trainings compliance view.
 * Used by Crew Duty Roster (pilots), Staff details Training tab (suite on),
 * and /staff-training (suite off) for ground staff.
 *
 * @param {'certificates'|'trainings'|null} sectionFocus
 *   When set (staff hub), lock UI to that work area and simplify actions.
 */
function CrewTrainingUpdatesPanel({
  crewMembers = [],
  selfCrewId = null,
  variant = 'crew',
  members = null,
  sectionFocus = null
}) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = variant === 'staff';
  const focused = isStaff && (sectionFocus === 'certificates' || sectionFocus === 'trainings');
  const focusCertificates = sectionFocus === 'certificates';
  const focusTrainings = sectionFocus === 'trainings';
  const people = members || crewMembers || [];
  const isOps = !isStaff && roleMatches(user?.role, [
    ROLES.OPERATIONS_MANAGER,
    ROLES.OPERATIONS_EXECUTIVE,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ]);
  const isStaffManager = isStaff && roleMatches(user?.role, [
    ROLES.SUPER_ADMIN,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.HR_MANAGER,
    ROLES.SAFETY_COFS,
    ROLES.SAFETY_MANAGER,
    ROLES.SAFETY_OFFICER
  ]);
  const isManager = isStaff ? isStaffManager : isOps;
  const canEdit = isManager || Boolean(selfCrewId);

  const [rows, setRows] = useState([]);
  const [certificateRows, setCertificateRows] = useState([]);
  const [profileCrewOptions, setProfileCrewOptions] = useState(people);
  const [loading, setLoading] = useState(false);
  const [crewFilter, setCrewFilter] = useState(selfCrewId || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [kindFilter, setKindFilter] = useState(
    sectionFocus === 'certificates' ? 'CERTIFICATE' : sectionFocus === 'trainings' ? 'TRAINING' : 'ALL'
  );
  const [innerTab, setInnerTab] = useState(
    sectionFocus === 'certificates' ? 1 : sectionFocus === 'trainings' ? 2 : 0
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const profileCrewId = selfCrewId || crewFilter || '';
  const canManageCertificates = isStaff
    ? roleMatches(user?.role, [
      ROLES.SUPER_ADMIN,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.HR_MANAGER,
      ROLES.SAFETY_COFS,
      ROLES.SAFETY_MANAGER,
      ROLES.SAFETY_OFFICER
    ])
    : roleMatches(user?.role, [
      ROLES.SUPER_ADMIN,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.OPERATIONS_MANAGER,
      ROLES.OPERATIONS_EXECUTIVE
    ]);
  const canEditProfileCertificates =
    Boolean(profileCrewId) &&
    (String(user?._id) === String(profileCrewId) || canManageCertificates);
  const canActOnItem = (item) => {
    if (!item) return false;
    if (String(user?._id) === String(item.crewId)) return true;
    return canManageCertificates;
  };

  const personNoun = isStaff ? 'employee' : 'crew';
  const personLabel = isStaff ? 'Employee' : 'Crew';
  const scopeAllLabel = isStaff ? 'All staff' : 'All crew';
  const complianceTitle = isStaff ? 'Staff compliance' : 'Crew compliance';
  const canEditTrainingCoreFields = roleMatches(user?.role, [
    ROLES.SUPER_ADMIN,
    ROLES.ACCOUNTABLE_MANAGER,
    ...(isStaff
      ? [
        ROLES.HR_MANAGER,
        ROLES.SAFETY_COFS,
        ROLES.SAFETY_MANAGER,
        ROLES.SAFETY_OFFICER
      ]
      : [])
  ]);

  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [certEditItem, setCertEditItem] = useState(null);
  const [certForm, setCertForm] = useState(emptyCertForm);
  const [certError, setCertError] = useState('');
  const [certSaving, setCertSaving] = useState(false);
  const [reportDownloading, setReportDownloading] = useState(false);
  const [duePdfDownloading, setDuePdfDownloading] = useState(false);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: 60 };
      if (crewFilter) {
        if (isStaff) params.userId = crewFilter;
        else params.crewId = crewFilter;
      }
      const trainingsUrl = isStaff
        ? `${API_URL}/hrms/profile-trainings`
        : `${API_URL}/ops/crew-duty-roster/trainings`;
      const certsUrl = isStaff
        ? `${API_URL}/hrms/profile-certificates`
        : `${API_URL}/ops/crew-duty-roster/certificates`;
      const requests = [
        axios.get(trainingsUrl, { headers: authHeaders, params }),
        axios.get(certsUrl, { headers: authHeaders, params })
      ];
      if (isOps && !isStaff) {
        requests.push(
          axios
            .get(`${API_URL}/ops/crew-duty-roster/crew-profiles`, { headers: authHeaders })
            .catch(() => ({ data: people }))
        );
      }
      const [trainRes, certRes, crewRes] = await Promise.all(requests);
      setRows(trainRes.data || []);
      setCertificateRows(certRes.data || []);
      if (isStaff) {
        setProfileCrewOptions(people);
      } else if (crewRes?.data) {
        setProfileCrewOptions(crewRes.data || []);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load certificates / trainings', 'error');
      setRows([]);
      setCertificateRows([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, crewFilter, showToast, isOps, isStaff, people]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isStaff) setProfileCrewOptions(people);
  }, [isStaff, people]);

  const downloadReport = async () => {
    try {
      setReportDownloading(true);
      const params = { days: 60 };
      if (crewFilter) params.crewId = crewFilter;
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(
        `${API_URL}/ops/crew-duty-roster/certificates-trainings-report`,
        {
          headers: authHeaders,
          params,
          responseType: 'blob'
        }
      );

      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        let message = 'Failed to download report';
        try {
          message = JSON.parse(text)?.message || message;
        } catch {
          if (text) message = text;
        }
        throw new Error(message);
      }

      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename =
        match?.[1] ||
        `pilots-certs-trainings-report_${dayjs().format('YYYY-MM-DD')}.xlsx`;

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      showToast('Report downloaded', 'success');
    } catch (err) {
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          showToast(parsed?.message || 'Failed to download report', 'error');
          return;
        } catch {
          // fall through
        }
      }
      showToast(err.response?.data?.message || err.message || 'Failed to download report', 'error');
    } finally {
      setReportDownloading(false);
    }
  };

  /**
   * Download or view PDF of due (expired + expiring) certificates & trainings.
   * @param {{ crewId?: string|null, crewName?: string, view?: boolean }} [opts]
   */
  const downloadDuePdf = async (opts = {}) => {
    const hasExplicitCrew = Object.prototype.hasOwnProperty.call(opts, 'crewId');
    const targetCrewId = hasExplicitCrew
      ? opts.crewId || undefined
      : selfCrewId || crewFilter || undefined;
    const labelForFile = opts.crewName
      || (targetCrewId
        ? (() => {
            const c = profileCrewOptions.find((x) => String(x._id) === String(targetCrewId));
            return c
              ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'crew'
              : 'crew';
          })()
        : 'all-crew');
    const viewOnly = Boolean(opts.view);

    try {
      setDuePdfDownloading(true);
      const params = new URLSearchParams();
      params.set('days', '60');
      params.set('download', viewOnly ? '0' : '1');
      if (targetCrewId) params.set('crewId', String(targetCrewId));
      const safe = String(labelForFile).replace(/[^\w.-]+/g, '_').slice(0, 40);
      const filename = `due-certs-trainings_${safe}_${dayjs().format('YYYY-MM-DD')}.pdf`;
      const url = `${API_URL}/ops/crew-duty-roster/certificates-trainings-due.pdf?${params}`;
      if (viewOnly) {
        try {
          await viewAuthenticatedPdf(url);
        } catch (err) {
          openAuthenticatedPdfViaToken(
            url.startsWith('/')
              ? url
              : `/api/ops/crew-duty-roster/certificates-trainings-due.pdf?${params}`
          );
        }
        showToast('Opened due certificates PDF', 'success');
      } else {
        await downloadAuthenticatedPdf(url, filename);
        showToast(
          targetCrewId ? 'Due certificates PDF downloaded for pilot' : 'Due certificates PDF downloaded for all crew',
          'success'
        );
      }
    } catch (err) {
      showToast(err.message || 'Failed to open due certificates PDF', 'error');
    } finally {
      setDuePdfDownloading(false);
    }
  };

  const combinedItems = useMemo(() => {
    const certs = (certificateRows || []).map((c) => ({
      key: `certificate-${c.crewId}-${c._id}`,
      kind: 'certificate',
      _id: c._id,
      crewId: c.crewId,
      crewName: c.crewName,
      baseCity: c.baseCity,
      name: c.name,
      typeLabel: (c.type || '').replace(/_/g, ' ') || '—',
      acquiredDate: c.acquiredDate,
      expiryDate: c.expiryDate,
      hasNoExpiry: c.hasNoExpiry,
      reminderDates: [],
      status: c.status,
      daysRemaining: c.daysRemaining,
      fileUrl: c.fileUrl,
      fileName: c.fileName,
      mimeType: c.mimeType,
      certificateUrl: c.certificateUrl,
      remarks: c.remarks,
      source: c.source || 'user',
      onboardingId: c.onboardingId || null,
      hasFile: Boolean(c.hasFile || c.fileName || c.fileUrl),
      hasExternalUrl: Boolean(c.hasExternalUrl || c.certificateUrl),
      previousVersions: c.previousVersions || [],
      versionCount: c.versionCount || (c.previousVersions || []).length,
      raw: c
    }));
    const trains = (rows || []).map((t) => ({
      key: `training-${t.crewId}-${t._id}`,
      kind: 'training',
      _id: t._id,
      crewId: t.crewId,
      crewName: t.crewName,
      baseCity: t.baseCity,
      name: t.trainingName,
      typeLabel: t.trainingType || '—',
      acquiredDate: t.acquiredDate,
      expiryDate: t.expiryDate,
      hasNoExpiry: t.hasNoExpiry,
      reminderDates: t.reminderDates || [],
      status: t.status,
      daysRemaining: t.daysRemaining,
      fileUrl: t.fileUrl,
      fileName: t.fileName,
      mimeType: t.mimeType,
      certificateUrl: t.certificateUrl,
      remarks: t.remarks,
      trainingId: t.trainingId,
      source: t.source || 'user',
      onboardingId: t.onboardingId || null,
      hasFile: Boolean(t.hasFile || t.fileName || t.fileUrl),
      hasExternalUrl: Boolean(t.hasExternalUrl || t.certificateUrl),
      raw: t
    }));
    const all = [...certs, ...trains];
    all.sort((a, b) => {
      const da = a.daysRemaining == null ? Number.POSITIVE_INFINITY : a.daysRemaining;
      const db = b.daysRemaining == null ? Number.POSITIVE_INFINITY : b.daysRemaining;
      if (da !== db) return da - db;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
    return all;
  }, [certificateRows, rows]);

  const filteredCombined = useMemo(() => {
    return combinedItems.filter((item) => {
      if (kindFilter === 'CERTIFICATE' && item.kind !== 'certificate') return false;
      if (kindFilter === 'TRAINING' && item.kind !== 'training') return false;
      if (statusFilter && item.status !== statusFilter) return false;
      return true;
    });
  }, [combinedItems, kindFilter, statusFilter]);

  const summary = useMemo(() => {
    const certs = certificateRows || [];
    const trains = rows || [];
    const all = [...certs, ...trains];
    return {
      certificates: certs.length,
      trainings: trains.length,
      expired: all.filter((i) => i.status === 'EXPIRED').length,
      expiring: all.filter((i) => i.status === 'EXPIRING').length,
      ok: all.filter((i) => i.status === 'OK' || i.status === 'NO_EXPIRY').length
    };
  }, [certificateRows, rows]);

  const expiryAlerts = useMemo(() => {
    const alerts = combinedItems.filter((i) => i.status === 'EXPIRED' || i.status === 'EXPIRING');
    return {
      expired: alerts.filter((i) => i.status === 'EXPIRED'),
      expiring: alerts.filter((i) => i.status === 'EXPIRING')
    };
  }, [combinedItems]);

  const displayTrainings = useMemo(() => {
    if (!statusFilter) return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const displayCertificates = useMemo(() => {
    if (!statusFilter) return certificateRows;
    return certificateRows.filter((c) => c.status === statusFilter);
  }, [certificateRows, statusFilter]);

  const selectedCrewLabel = useMemo(() => {
    if (!profileCrewId) return scopeAllLabel;
    const c = profileCrewOptions.find((x) => String(x._id) === String(profileCrewId));
    if (!c) return `Selected ${personNoun}`;
    return `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email;
  }, [profileCrewId, profileCrewOptions, scopeAllLabel, personNoun]);

  useEffect(() => {
    if (sectionFocus === 'certificates') {
      setInnerTab(1);
      setKindFilter('CERTIFICATE');
    } else if (sectionFocus === 'trainings') {
      setInnerTab(2);
      setKindFilter('TRAINING');
    }
  }, [sectionFocus]);

  const openCreate = () => {
    setEditRow(null);
    setForm({ ...emptyForm, crewId: selfCrewId || crewFilter || '' });
    setError('');
    setDialogOpen(true);
  };

  const openAddCertificate = () => {
    const targetId = selfCrewId || crewFilter;
    if (!targetId) {
      showToast(`Select an ${personNoun} first to upload a certificate`, 'info');
      return;
    }
    setInnerTab(1);
    setCrewFilter(targetId);
  };

  const openEdit = (row) => {
    const src = row.raw || row;
    setEditRow(src);
    setForm({
      crewId: src.crewId,
      trainingId: src.trainingId || '',
      trainingName: src.trainingName || src.name || '',
      trainingType: src.trainingType || 'REQUIRED',
      acquiredDate: src.acquiredDate ? dayjs(src.acquiredDate).format('YYYY-MM-DD') : '',
      performedOnDate: src.performedOnDate ? dayjs(src.performedOnDate).format('YYYY-MM-DD') : '',
      expiryDate: src.expiryDate ? dayjs(src.expiryDate).format('YYYY-MM-DD') : '',
      periodicityMonths: src.periodicityMonths != null ? String(src.periodicityMonths) : '',
      hasNoExpiry: Boolean(src.hasNoExpiry),
      reminderDates: (src.reminderDates || []).map((d) => dayjs(d).format('YYYY-MM-DD')),
      reminderDateInput: '',
      certificateUrl: src.certificateUrl || '',
      remarks: src.remarks || '',
      file: null
    });
    setError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditRow(null);
    setForm(emptyForm);
    setError('');
  };

  const openItemAction = (item) => {
    if (!item) return;
    if (item.kind === 'training') {
      openEdit(item);
      return;
    }
    // Certificate renew / update
    const src = item.raw || item;
    const months = src.periodicityMonths != null ? String(src.periodicityMonths) : '12';
    const acquired = dayjs().format('YYYY-MM-DD');
    const period = parseInt(months, 10);
    const nextExpiry =
      !src.hasNoExpiry && Number.isFinite(period) && period > 0
        ? dayjs(acquired).add(period, 'month').format('YYYY-MM-DD')
        : (src.expiryDate ? dayjs(src.expiryDate).format('YYYY-MM-DD') : '');
    setCertEditItem(item);
    setCertForm({
      crewId: item.crewId,
      certificateId: item._id,
      onboardingId: item.onboardingId || null,
      source: item.source || 'user',
      name: item.name || '',
      type: src.type || item.typeLabel || 'OTHER',
      acquiredDate: acquired,
      performedOnDate: dayjs().format('YYYY-MM-DD'),
      expiryDate: src.hasNoExpiry ? '' : nextExpiry,
      periodicityMonths: months,
      hasNoExpiry: Boolean(src.hasNoExpiry),
      certificateUrl: item.certificateUrl || '',
      remarks: item.remarks || '',
      file: null
    });
    setCertError('');
    setCertDialogOpen(true);
  };

  const closeCertDialog = () => {
    setCertDialogOpen(false);
    setCertEditItem(null);
    setCertForm(emptyCertForm);
    setCertError('');
  };

  useEffect(() => {
    if (certForm.hasNoExpiry || !certDialogOpen) return;
    const months = parseInt(certForm.periodicityMonths, 10);
    if (!Number.isFinite(months) || months <= 0 || !certForm.acquiredDate) return;
    const nextExpiry = dayjs(certForm.acquiredDate).add(months, 'month').format('YYYY-MM-DD');
    setCertForm((prev) => (prev.expiryDate === nextExpiry ? prev : { ...prev, expiryDate: nextExpiry }));
  }, [certForm.acquiredDate, certForm.periodicityMonths, certForm.hasNoExpiry, certDialogOpen]);

  const handleCertSubmit = async () => {
    try {
      setCertSaving(true);
      setCertError('');
      if (!certForm.name?.trim()) {
        setCertError('Certificate name is required.');
        return;
      }
      if (!certForm.acquiredDate) {
        setCertError('Acquired / renewal date is required.');
        return;
      }
      if (!certForm.file) {
        setCertError('A new certificate file must be uploaded when renewing. Previous files stay available.');
        return;
      }
      if (!certForm.hasNoExpiry) {
        if (!certForm.periodicityMonths || !parseInt(certForm.periodicityMonths, 10)) {
          setCertError('Periodicity (months) is required when certificate expires.');
          return;
        }
        if (!certForm.expiryDate) {
          setCertError('Expiry date is required.');
          return;
        }
      }

      const mode = certForm.source === 'onboarding' ? 'onboarding' : 'user';
      const entityId =
        mode === 'onboarding' && certForm.onboardingId ? certForm.onboardingId : certForm.crewId;
      const endpoint =
        mode === 'onboarding'
          ? `${API_URL}/users/onboard/${entityId}/certificates/${certForm.certificateId}`
          : `${API_URL}/users/${entityId}/certificates/${certForm.certificateId}`;

      const data = new FormData();
      data.append('file', certForm.file);
      data.append('isRenewal', 'true');
      data.append('name', certForm.name.trim());
      data.append('type', certForm.type || 'OTHER');
      data.append('acquiredDate', certForm.acquiredDate);
      data.append('performedOnDate', certForm.performedOnDate || '');
      data.append('hasNoExpiry', certForm.hasNoExpiry ? 'true' : 'false');
      if (!certForm.hasNoExpiry) {
        data.append('expiryDate', certForm.expiryDate);
        data.append('periodicityMonths', String(parseInt(certForm.periodicityMonths, 10)));
      }
      if (certForm.certificateUrl) data.append('certificateUrl', certForm.certificateUrl.trim());
      data.append(
        'remarks',
        certForm.remarks?.trim() ||
          `Renewed via ${isStaff ? 'Staff details' : 'Crew Duty'} Certs & Trainings on ${dayjs().format('YYYY-MM-DD')}`
        );

      await axios.put(endpoint, data, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      showToast('Certificate renewed — previous file kept in history', 'success');
      closeCertDialog();
      load();
    } catch (err) {
      setCertError(err.response?.data?.message || 'Failed to update certificate.');
    } finally {
      setCertSaving(false);
    }
  };

  useEffect(() => {
    if (form.hasNoExpiry || !dialogOpen) return;
    const months = parseInt(form.periodicityMonths, 10);
    if (!Number.isFinite(months) || months <= 0 || !form.acquiredDate) return;
    const nextExpiry = dayjs(form.acquiredDate).add(months, 'month').format('YYYY-MM-DD');
    setForm((prev) => (prev.expiryDate === nextExpiry ? prev : { ...prev, expiryDate: nextExpiry }));
  }, [form.acquiredDate, form.periodicityMonths, form.hasNoExpiry, dialogOpen]);

  const addReminderDate = () => {
    const value = (form.reminderDateInput || '').trim();
    if (!value) return;
    if (!dayjs(value).isValid()) {
      setError('Invalid reminder date.');
      return;
    }
    setForm((prev) => {
      if (prev.reminderDates.includes(value)) return prev;
      return { ...prev, reminderDates: [...prev.reminderDates, value].sort(), reminderDateInput: '' };
    });
    setError('');
  };

  const removeReminderDate = (date) => {
    setForm((prev) => ({
      ...prev,
      reminderDates: prev.reminderDates.filter((d) => d !== date)
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      if (!editRow && !form.crewId) {
        setError('Crew member is required.');
        return;
      }
      if (!form.trainingId?.trim() || !form.trainingName?.trim()) {
        setError('Training ID and name are required.');
        return;
      }
      if (!form.acquiredDate) {
        setError('Acquired date is required.');
        return;
      }
      if (!form.hasNoExpiry) {
        if (!form.periodicityMonths || !parseInt(form.periodicityMonths, 10)) {
          setError('Periodicity (months) is required when training expires.');
          return;
        }
        if (!form.expiryDate) {
          setError('Expiry date is required when training expires.');
          return;
        }
        if (!form.reminderDates.length) {
          setError('At least one reminder date is required when training expires.');
          return;
        }
      }

      const targetUserId = editRow ? editRow.crewId : form.crewId;
      const data = new FormData();
      if (form.file) data.append('file', form.file);
      data.append('trainingId', form.trainingId.trim());
      data.append('trainingName', form.trainingName.trim());
      data.append('trainingType', form.trainingType || 'REQUIRED');
      data.append('acquiredDate', form.acquiredDate);
      data.append('performedOnDate', form.performedOnDate || '');
      data.append('hasNoExpiry', form.hasNoExpiry ? 'true' : 'false');
      if (!form.hasNoExpiry) {
        data.append('expiryDate', form.expiryDate);
        data.append('periodicityMonths', String(parseInt(form.periodicityMonths, 10)));
        data.append('reminderDates', JSON.stringify(form.reminderDates));
      } else {
        data.append('reminderDates', JSON.stringify([]));
      }
      if (form.certificateUrl) data.append('certificateUrl', form.certificateUrl.trim());
      if (form.remarks) data.append('remarks', form.remarks.trim());

      if (editRow) {
        await axios.put(`${API_URL}/users/${targetUserId}/trainings/${editRow._id}`, data, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
        });
        showToast('Training updated', 'success');
      } else {
        await axios.post(`${API_URL}/users/${targetUserId}/trainings`, data, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
        });
        showToast('Training added', 'success');
      }
      closeDialog();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save training.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const name = row.trainingName || row.name;
    if (!window.confirm(`Delete training "${name}" for ${row.crewName}?`)) return;
    try {
      await axios.delete(`${API_URL}/users/${row.crewId}/trainings/${row._id}`, { headers: authHeaders });
      showToast('Training deleted', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete training', 'error');
    }
  };

  const fileErrorMessage = (error) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      return error.message || 'File not found';
    }
    return data?.message || error.message || 'File not found';
  };

  const resolveFileTarget = (item) => {
    const mode = item.source === 'onboarding' ? 'onboarding' : 'user';
    const entityId =
      item.source === 'onboarding' && item.onboardingId ? item.onboardingId : item.crewId;
    const resource = item.kind === 'training' ? 'trainings' : 'certificates';
    return { mode, entityId, resource };
  };

  const handlePreviewFile = async (item) => {
    try {
      const { mode, entityId, resource } = resolveFileTarget(item);
      await previewUploadedFile({
        mode,
        entityId,
        resource,
        itemId: item._id,
        mimeType: item.mimeType
      });
    } catch (error) {
      showToast(fileErrorMessage(error), 'error');
    }
  };

  const handleDownloadFile = async (item) => {
    try {
      const { mode, entityId, resource } = resolveFileTarget(item);
      await downloadUploadedFile({
        mode,
        entityId,
        resource,
        itemId: item._id,
        fileName: item.fileName || item.name || item.trainingName || resource,
        mimeType: item.mimeType
      });
    } catch (error) {
      showToast(fileErrorMessage(error), 'error');
    }
  };

  const renderFileActions = (item, { emphasizeDownload = false } = {}) => {
    const canPreviewFile = Boolean(item.hasFile || item.fileUrl || item.fileName);

    if (canPreviewFile) {
      if (emphasizeDownload || item.kind === 'certificate') {
        return (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={<GetApp />}
              onClick={() => handleDownloadFile(item)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Download
            </Button>
            <Tooltip title="Preview">
              <IconButton size="small" color="primary" onClick={() => handlePreviewFile(item)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      }
      return (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Preview">
            <IconButton size="small" onClick={() => handlePreviewFile(item)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton size="small" onClick={() => handleDownloadFile(item)}>
              <GetApp fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    }
    if (item.hasExternalUrl || item.certificateUrl) {
      return (
        <Tooltip title="Open URL">
          <IconButton size="small" href={item.certificateUrl} target="_blank" component="a">
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <Tooltip title="No file uploaded">
        <Typography variant="caption" color="text.disabled">
          No file
        </Typography>
      </Tooltip>
    );
  };

  return (
    <Box>
      {/* Page header */}
      {!focused && (
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)'
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontWeight: 700 }}
              >
                {complianceTitle}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B2A4A', mt: 0.25 }}>
                {selfCrewId ? 'My Certificates & Trainings' : 'Certificates & Trainings'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 640 }}>
                {isStaff
                  ? 'Unified view of ground-staff certificates and trainings from user profiles — expiry tracking, renewal reminders, and priority alerts.'
                  : 'Unified view of pilot certificates and trainings from user profiles — expiry tracking, renewal reminders, and priority alerts.'}
              </Typography>
              <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={`Scope: ${selectedCrewLabel}`} />
                <Chip size="small" variant="outlined" label={`${filteredCombined.length} records shown`} />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!isStaff && isOps && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={duePdfDownloading ? <CircularProgress size={16} /> : <PictureAsPdf />}
                  onClick={() => downloadDuePdf({ crewId: null })}
                  disabled={loading || duePdfDownloading}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {duePdfDownloading ? 'Preparing…' : 'Due PDF (all crews)'}
                </Button>
              )}
              {!isStaff && (selfCrewId || crewFilter) && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={duePdfDownloading ? <CircularProgress size={16} /> : <OpenInNew />}
                  onClick={() =>
                    downloadDuePdf({
                      crewId: selfCrewId || crewFilter,
                      crewName: selectedCrewLabel !== scopeAllLabel ? selectedCrewLabel : undefined,
                      view: true
                    })
                  }
                  disabled={loading || duePdfDownloading}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  View due PDF
                </Button>
              )}
              {!isStaff && (
                <Button
                  variant={selfCrewId ? 'contained' : 'outlined'}
                  color="warning"
                  startIcon={duePdfDownloading ? <CircularProgress size={16} /> : <PictureAsPdf />}
                  onClick={() =>
                    downloadDuePdf({
                      crewId: selfCrewId || crewFilter || undefined,
                      crewName: selectedCrewLabel !== scopeAllLabel ? selectedCrewLabel : undefined
                    })
                  }
                  disabled={
                    loading
                    || duePdfDownloading
                    || (isOps && !selfCrewId && !crewFilter)
                  }
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {duePdfDownloading
                    ? 'Preparing…'
                    : selfCrewId || crewFilter
                      ? `Download due PDF`
                      : 'Due PDF (select pilot)'}
                </Button>
              )}
              {!isStaff && (
                <Button
                  variant="outlined"
                  startIcon={reportDownloading ? <CircularProgress size={16} /> : <FileDownload />}
                  onClick={downloadReport}
                  disabled={loading || reportDownloading}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {reportDownloading ? 'Preparing…' : selfCrewId ? 'Download my report' : 'Download report'}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={load}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Refresh
              </Button>
              {canEdit && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreate}
                  sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#1B4F72', '&:hover': { bgcolor: '#154360' } }}
                >
                  Add Training
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      {focused && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'grey.50'
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={focusCertificates ? 'Certificate workspace' : 'Training workspace'}
              />
              <Chip size="small" variant="outlined" label={selectedCrewLabel} />
              <Chip
                size="small"
                variant="outlined"
                label={
                  focusCertificates
                    ? `${displayCertificates.length} certificates`
                    : `${displayTrainings.length} trainings`
                }
              />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={load}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Refresh
              </Button>
              {canEdit && focusTrainings && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreate}
                  sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#1B4F72', '&:hover': { bgcolor: '#154360' } }}
                >
                  Add training
                </Button>
              )}
              {canEdit && focusCertificates && (
                <Button
                  variant="contained"
                  startIcon={<WorkspacePremium />}
                  onClick={openAddCertificate}
                  disabled={!selfCrewId && !crewFilter}
                  sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#1B4F72', '&:hover': { bgcolor: '#154360' } }}
                >
                  {profileCrewId ? 'Manage / upload certificates' : 'Select employee to upload'}
                </Button>
              )}
            </Stack>
          </Stack>
          {focusCertificates && canEdit && !profileCrewId && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
              Choose an employee in the filter below, then use Manage / upload certificates to add or replace files.
            </Alert>
          )}
        </Paper>
      )}

      {/* Metrics */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2.5} useFlexGap flexWrap="wrap">
        {!focusTrainings && (
          <MetricTile
            label="Certificates"
            value={summary.certificates}
            accent={ACCENT.certificates}
            icon={<WorkspacePremium fontSize="small" />}
          />
        )}
        {!focusCertificates && (
          <MetricTile
            label="Trainings"
            value={summary.trainings}
            accent={ACCENT.trainings}
            icon={<School fontSize="small" />}
          />
        )}
        <MetricTile
          label="Expired"
          value={summary.expired}
          accent={ACCENT.expired}
          icon={<ErrorOutline fontSize="small" />}
        />
        <MetricTile
          label="Within 60 days"
          value={summary.expiring}
          accent={ACCENT.expiring}
          icon={<Schedule fontSize="small" />}
        />
        <MetricTile
          label="Valid"
          value={summary.ok}
          accent={ACCENT.ok}
          icon={<CheckCircleOutline fontSize="small" />}
        />
      </Stack>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
      >
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          {focused ? 'Filter this section' : 'Filters'}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          {!selfCrewId && (
            <Grid item xs={12} md={focused ? 6 : 4}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <FormControl fullWidth size="small">
                  <InputLabel>{personLabel}</InputLabel>
                  <Select
                    value={crewFilter}
                    label={personLabel}
                    onChange={(e) => setCrewFilter(e.target.value)}
                  >
                    <MenuItem value="">{scopeAllLabel}</MenuItem>
                    {profileCrewOptions.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.firstName} {c.lastName}
                        {c.isActive === false ? ' (inactive)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!isStaff && (
                  <Tooltip title={crewFilter ? `Download due PDF for ${selectedCrewLabel}` : `Select a ${personNoun} to download their due PDF`}>
                    <span>
                      <IconButton
                        color="warning"
                        onClick={() =>
                          downloadDuePdf({
                            crewId: crewFilter,
                            crewName: selectedCrewLabel
                          })
                        }
                        disabled={!crewFilter || duePdfDownloading || loading}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mt: 0.25
                        }}
                      >
                        {duePdfDownloading ? <CircularProgress size={18} /> : <PictureAsPdf />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Grid>
          )}
          {!focused && (
            <Grid item xs={12} md={!selfCrewId ? 4 : 6}>
              <FormControl fullWidth size="small">
                <InputLabel>Record type</InputLabel>
                <Select value={kindFilter} label="Record type" onChange={(e) => setKindFilter(e.target.value)}>
                  <MenuItem value="ALL">Certificates & trainings</MenuItem>
                  <MenuItem value="CERTIFICATE">Certificates only</MenuItem>
                  <MenuItem value="TRAINING">Trainings only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={focused ? (!selfCrewId ? 6 : 12) : (!selfCrewId ? 4 : 6)}>
            <FormControl fullWidth size="small">
              <InputLabel>Expiry status</InputLabel>
              <Select value={statusFilter} label="Expiry status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="EXPIRED">Expired</MenuItem>
                <MenuItem value="EXPIRING">Expiring (≤60 days)</MenuItem>
                <MenuItem value="OK">Valid</MenuItem>
                <MenuItem value="NO_EXPIRY">No expiry</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} md={8} lg={9}>
            <Paper
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}
            >
              {!focused && (
                <Box sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <Tabs
                    value={innerTab}
                    onChange={(e, v) => setInnerTab(v)}
                    sx={{
                      minHeight: 48,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        minHeight: 48,
                        fontSize: '0.9rem'
                      }
                    }}
                  >
                    <Tab label="Overview" />
                    <Tab
                      icon={<WorkspacePremium sx={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label="Certificates"
                    />
                    <Tab icon={<School sx={{ fontSize: 18 }} />} iconPosition="start" label="Trainings" />
                  </Tabs>
                </Box>
              )}

              {innerTab === 0 && (
                <Box>
                  <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Combined compliance register
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Certificates and trainings sorted by closest expiry. Click column filters above to narrow the list.
                    </Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table size="small" stickyHeader>
                      <TableHead sx={tableHeadSx}>
                        <TableRow>
                          {!selfCrewId && <TableCell>{personLabel}</TableCell>}
                          <TableCell>Kind</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Acquired</TableCell>
                          <TableCell>Expiry</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Reminders</TableCell>
                          <TableCell>File</TableCell>
                          {canEdit && <TableCell align="right">Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCombined.map((item, idx) => (
                          <TableRow
                            key={item.key}
                            hover
                            sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50' }}
                          >
                            {!selfCrewId && (
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {item.crewName}
                                </Typography>
                                {item.baseCity && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.baseCity}
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <Chip
                                size="small"
                                label={item.kind === 'certificate' ? 'Certificate' : 'Training'}
                                sx={{
                                  fontWeight: 600,
                                  bgcolor: item.kind === 'certificate' ? 'rgba(27,79,114,0.08)' : 'rgba(26,82,118,0.08)',
                                  color: '#1B4F72'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {item.name}
                              </Typography>
                              {item.kind === 'training' && item.trainingId && (
                                <Typography variant="caption" color="text.secondary">
                                  {item.trainingId}
                                </Typography>
                              )}
                              {item.source === 'onboarding' && (
                                <Chip size="small" label="Onboarding" variant="outlined" sx={{ mt: 0.5, height: 20 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.typeLabel}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(item.acquiredDate)}</Typography>
                            </TableCell>
                            <TableCell>
                              {item.hasNoExpiry ? (
                                <Typography variant="body2" color="text.secondary">
                                  No expiry
                                </Typography>
                              ) : (
                                <Typography variant="body2" fontWeight={600}>
                                  {formatDate(item.expiryDate)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusChip status={item.status} daysRemaining={item.daysRemaining} />
                            </TableCell>
                            <TableCell>
                              {item.kind !== 'training' || item.hasNoExpiry ? (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              ) : (item.reminderDates || []).length ? (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {item.reminderDates.slice(0, 2).map((d) => (
                                    <Chip key={String(d)} size="small" label={formatDate(d)} variant="outlined" />
                                  ))}
                                  {item.reminderDates.length > 2 && (
                                    <Chip size="small" label={`+${item.reminderDates.length - 2}`} />
                                  )}
                                </Stack>
                              ) : (
                                <Chip size="small" color="warning" label="Missing" />
                              )}
                            </TableCell>
                            <TableCell>{renderFileActions(item)}</TableCell>
                            {canEdit && (
                              <TableCell align="right">
                                {item.kind === 'training' ? (
                                  <>
                                    <Tooltip title="Edit training">
                                      <IconButton size="small" onClick={() => openEdit(item)}>
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : canActOnItem(item) ? (
                                  <Tooltip
                                    title={
                                      item.status === 'EXPIRED' || item.status === 'EXPIRING'
                                        ? 'Renew / update certificate'
                                        : 'Update certificate'
                                    }
                                  >
                                    <Button
                                      size="small"
                                      variant={
                                        item.status === 'EXPIRED' || item.status === 'EXPIRING'
                                          ? 'contained'
                                          : 'outlined'
                                      }
                                      color={item.status === 'EXPIRED' ? 'error' : item.status === 'EXPIRING' ? 'warning' : 'primary'}
                                      startIcon={<Update />}
                                      onClick={() => openItemAction(item)}
                                      sx={{ textTransform: 'none', fontWeight: 600 }}
                                    >
                                      {item.status === 'EXPIRED'
                                        ? 'Renew'
                                        : item.status === 'EXPIRING'
                                          ? 'Update'
                                          : 'Edit'}
                                    </Button>
                                  </Tooltip>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        {filteredCombined.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={selfCrewId ? 9 : 10} align="center" sx={{ py: 6 }}>
                              <Typography color="text.secondary">
                                No certificates or trainings match the current filters.
                              </Typography>
                              <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={() => { setStatusFilter(''); setKindFilter('ALL'); }}>
                                Clear filters
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {innerTab === 1 && (
                <Box p={2.5}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Certificates
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {profileCrewId
                      ? `Profile certificates for ${selectedCrewLabel} — same records as Users.`
                      : (isStaff
                        ? 'All staff certificates from profiles. Select an employee to manage uploads.'
                        : 'All crew certificates from profiles. Select a crew member to manage uploads.')}
                  </Typography>
                  {profileCrewId ? (
                    <CertificateManagement
                      key={profileCrewId}
                      entityId={profileCrewId}
                      mode="user"
                      readOnly={!canEditProfileCertificates}
                      userRole={user?.role}
                      hideTitle
                    />
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={tableHeadSx}>
                          <TableRow>
                            <TableCell>{personLabel}</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Acquired</TableCell>
                            <TableCell>Expiry</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>File</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayCertificates.map((cert, idx) => (
                            <TableRow
                              key={`c-${cert.crewId}-${cert._id}`}
                              hover
                              sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50' }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {cert.crewName}
                                </Typography>
                              </TableCell>
                              <TableCell>{cert.name}</TableCell>
                              <TableCell>{(cert.type || '').replace(/_/g, ' ') || '—'}</TableCell>
                              <TableCell>{formatDate(cert.acquiredDate)}</TableCell>
                              <TableCell>
                                {cert.hasNoExpiry ? 'No expiry' : formatDate(cert.expiryDate)}
                              </TableCell>
                              <TableCell>
                                <StatusChip status={cert.status} daysRemaining={cert.daysRemaining} />
                              </TableCell>
                              <TableCell>
                                {renderFileActions({ ...cert, kind: 'certificate', _id: cert._id })}
                              </TableCell>
                            </TableRow>
                          ))}
                          {displayCertificates.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                <Typography color="text.secondary">No certificates on file.</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {innerTab === 2 && (
                <Box p={2.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Trainings
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ongoing trainings with required reminder dates when expiring.
                      </Typography>
                    </Box>
                    {canEdit && (
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={openCreate}
                        variant="outlined"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Add training
                      </Button>
                    )}
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={tableHeadSx}>
                        <TableRow>
                          {!selfCrewId && <TableCell>{personLabel}</TableCell>}
                          <TableCell>Training</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Acquired</TableCell>
                          <TableCell>Expiry</TableCell>
                          <TableCell>Reminders</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>File</TableCell>
                          {canEdit && <TableCell align="right">Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayTrainings.map((row, idx) => (
                          <TableRow
                            key={`t-${row.crewId}-${row._id}`}
                            hover
                            sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50' }}
                          >
                            {!selfCrewId && (
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {row.crewName}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {row.trainingName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.trainingId}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.trainingType || '—'}</TableCell>
                            <TableCell>{formatDate(row.acquiredDate)}</TableCell>
                            <TableCell>
                              {row.hasNoExpiry ? 'No expiry' : formatDate(row.expiryDate)}
                            </TableCell>
                            <TableCell>
                              {(row.reminderDates || []).length === 0 ? (
                                row.hasNoExpiry ? (
                                  '—'
                                ) : (
                                  <Chip size="small" color="warning" label="Missing" />
                                )
                              ) : (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {row.reminderDates.map((d) => (
                                    <Chip key={String(d)} size="small" label={formatDate(d)} variant="outlined" />
                                  ))}
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusChip status={row.status} daysRemaining={row.daysRemaining} />
                            </TableCell>
                            <TableCell>
                              {renderFileActions({ ...row, kind: 'training', name: row.trainingName })}
                            </TableCell>
                            {canEdit && (
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => openEdit(row)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        {displayTrainings.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={selfCrewId ? 8 : 9} align="center" sx={{ py: 5 }}>
                              <Typography color="text.secondary">No trainings found.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Alerts rail */}
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                overflow: 'hidden',
                position: { md: 'sticky' },
                top: { md: 16 },
                maxHeight: { md: 'calc(100vh - 120px)' },
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.75,
                  bgcolor: '#FBF6E9',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WarningAmber sx={{ color: ACCENT.expiring, fontSize: 20 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Expiry alerts
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isStaff
                          ? 'Expired and due within 60 days'
                          : 'Expired and due within 60 days — download due PDF from the header'}
                      </Typography>
                    </Box>
                  </Stack>
                  {!isStaff && (
                    <Tooltip title={crewFilter || selfCrewId ? `Due PDF — ${selectedCrewLabel}` : 'Due PDF — all crews'}>
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() =>
                            downloadDuePdf(
                              isOps && !crewFilter && !selfCrewId
                                ? { crewId: null }
                                : {
                                    crewId: selfCrewId || crewFilter || undefined,
                                    crewName: selectedCrewLabel !== scopeAllLabel ? selectedCrewLabel : undefined
                                  }
                            )
                          }
                          disabled={loading || duePdfDownloading}
                        >
                          {duePdfDownloading ? <CircularProgress size={16} /> : <PictureAsPdf fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
              <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: ACCENT.expired,
                    display: 'block',
                    mb: 1
                  }}
                >
                  Expired · {expiryAlerts.expired.length}
                </Typography>
                <Stack spacing={1} mb={2.5}>
                  {expiryAlerts.expired.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      None
                    </Typography>
                  )}
                  {expiryAlerts.expired.map((item) => (
                    <AlertItem
                      key={item.key}
                      item={item}
                      showCrew={!selfCrewId}
                      canAct={canActOnItem(item)}
                      onAction={openItemAction}
                      onPreview={handlePreviewFile}
                      onDownload={handleDownloadFile}
                    />
                  ))}
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: ACCENT.expiring,
                    display: 'block',
                    mb: 1
                  }}
                >
                  Within 60 days · {expiryAlerts.expiring.length}
                </Typography>
                <Stack spacing={1}>
                  {expiryAlerts.expiring.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      None
                    </Typography>
                  )}
                  {expiryAlerts.expiring.map((item) => (
                    <AlertItem
                      key={item.key}
                      item={item}
                      showCrew={!selfCrewId}
                      canAct={canActOnItem(item)}
                      onAction={openItemAction}
                      onPreview={handlePreviewFile}
                      onDownload={handleDownloadFile}
                    />
                  ))}
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Dialog open={certDialogOpen} onClose={closeCertDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {certEditItem?.status === 'EXPIRED' ? 'Renew certificate' : 'Update certificate'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {certEditItem && (
              <Alert severity={certEditItem.status === 'EXPIRED' ? 'error' : 'warning'} variant="outlined">
                {certEditItem.crewName ? `${certEditItem.crewName} — ` : ''}
                {certEditItem.name}
                {certEditItem.expiryDate
                  ? ` (current expiry ${formatDate(certEditItem.expiryDate)})`
                  : ''}
              </Alert>
            )}
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              1 · Certificate details
            </Typography>
            <TextField
              label="Certificate name"
              value={certForm.name}
              onChange={(e) => setCertForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              select
              label="Certificate type"
              value={certForm.type}
              onChange={(e) => setCertForm((p) => ({ ...p, type: e.target.value }))}
              fullWidth
            >
              {CERTIFICATE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Divider />
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              2 · Validity
            </Typography>
            <TextField
              label="Acquired / renewal date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={certForm.acquiredDate}
              onChange={(e) => setCertForm((p) => ({ ...p, acquiredDate: e.target.value }))}
              fullWidth
              required
              helperText="Set to the new issue / renewal date"
            />
            <TextField
              label="Performed on date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={certForm.performedOnDate}
              onChange={(e) => setCertForm((p) => ({ ...p, performedOnDate: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="New expiry date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={certForm.hasNoExpiry ? '' : certForm.expiryDate}
                onChange={(e) => setCertForm((p) => ({ ...p, expiryDate: e.target.value }))}
                fullWidth
                disabled={certForm.hasNoExpiry}
                required={!certForm.hasNoExpiry}
              />
              <TextField
                select
                label="Has expiry?"
                value={certForm.hasNoExpiry ? 'no' : 'yes'}
                onChange={(e) => {
                  const expires = e.target.value === 'yes';
                  setCertForm((p) => ({
                    ...p,
                    hasNoExpiry: !expires,
                    periodicityMonths: expires ? p.periodicityMonths || '12' : '',
                    expiryDate: expires ? p.expiryDate : ''
                  }));
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="yes">Expires</MenuItem>
                <MenuItem value="no">No expiry</MenuItem>
              </TextField>
            </Stack>
            {!certForm.hasNoExpiry && (
              <TextField
                label="Periodicity (months)"
                type="number"
                value={certForm.periodicityMonths}
                onChange={(e) => setCertForm((p) => ({ ...p, periodicityMonths: e.target.value }))}
                fullWidth
                required
                inputProps={{ min: 1, step: 1 }}
                helperText="Used to calculate the next expiry from the renewal date"
              />
            )}
            <Divider />
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              3 · Upload certificate file
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: certForm.file ? 'rgba(25, 111, 61, 0.06)' : 'grey.50'
              }}
            >
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                A new file is required to renew or update. Prior versions stay available below.
              </Typography>
              <Button
                variant="contained"
                component="label"
                color={certForm.file ? 'success' : 'primary'}
                sx={{ textTransform: 'none' }}
              >
                {certForm.file
                  ? certForm.file.name
                  : 'Choose certificate file (required) *'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,image/*"
                  onChange={(e) => setCertForm((p) => ({ ...p, file: e.target.files?.[0] || null }))}
                />
              </Button>
            </Paper>
            {((certEditItem?.previousVersions || []).length > 0 || certEditItem?.hasFile) && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  bgcolor: 'grey.50'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Certificate history
                </Typography>
                {certEditItem?.hasFile && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: (certEditItem?.previousVersions || []).length ? 1 : 0 }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Current file
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {certEditItem.fileName || 'Uploaded certificate'}
                        {certEditItem.acquiredDate
                          ? ` · acquired ${formatDate(certEditItem.acquiredDate)}`
                          : ''}
                        {certEditItem.expiryDate
                          ? ` · expiry ${formatDate(certEditItem.expiryDate)}`
                          : ''}
                      </Typography>
                    </Box>
                    {renderFileActions(certEditItem)}
                  </Stack>
                )}
                {(certEditItem?.previousVersions || [])
                  .slice()
                  .reverse()
                  .map((version, idx) => {
                    const mode =
                      certEditItem.source === 'onboarding' ? 'onboarding' : 'user';
                    const entityId =
                      certEditItem.source === 'onboarding' && certEditItem.onboardingId
                        ? certEditItem.onboardingId
                        : certEditItem.crewId;
                    const canPreview = Boolean(version.hasFile || version.fileName || version.fileUrl);
                    return (
                      <Stack
                        key={version._id || idx}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: '1px dashed',
                          borderColor: 'divider'
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Previous version
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {version.fileName || 'Prior certificate'}
                            {version.acquiredDate
                              ? ` · acquired ${formatDate(version.acquiredDate)}`
                              : ''}
                            {version.expiryDate
                              ? ` · expiry ${formatDate(version.expiryDate)}`
                              : ''}
                            {version.archivedAt
                              ? ` · archived ${formatDate(version.archivedAt)}`
                              : ''}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {canPreview ? (
                            <>
                              <Tooltip title="Preview previous">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      await previewCertificateVersion({
                                        mode,
                                        entityId,
                                        certId: certEditItem._id,
                                        versionId: version._id,
                                        mimeType: version.mimeType
                                      });
                                    } catch (error) {
                                      showToast(fileErrorMessage(error), 'error');
                                    }
                                  }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download previous">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      await downloadCertificateVersion({
                                        mode,
                                        entityId,
                                        certId: certEditItem._id,
                                        versionId: version._id,
                                        fileName: version.fileName || 'previous-certificate',
                                        mimeType: version.mimeType
                                      });
                                    } catch (error) {
                                      showToast(fileErrorMessage(error), 'error');
                                    }
                                  }}
                                >
                                  <GetApp fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : version.certificateUrl ? (
                            <Tooltip title="Open URL">
                              <IconButton
                                size="small"
                                href={version.certificateUrl}
                                target="_blank"
                                component="a"
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No file
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
              </Box>
            )}
            <TextField
              label="Certificate URL (optional)"
              value={certForm.certificateUrl}
              onChange={(e) => setCertForm((p) => ({ ...p, certificateUrl: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Remarks"
              value={certForm.remarks}
              onChange={(e) => setCertForm((p) => ({ ...p, remarks: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            {certError && <Alert severity="error">{certError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCertDialog} disabled={certSaving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCertSubmit}
            variant="contained"
            disabled={certSaving || !certForm.file}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#1B4F72', '&:hover': { bgcolor: '#154360' } }}
          >
            Save renewal
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editRow ? 'Update training' : 'Add training'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              1 · Who &amp; what
            </Typography>
            {isManager && !editRow && (
              <TextField
                select
                label={personLabel}
                value={form.crewId}
                onChange={(e) => setForm((p) => ({ ...p, crewId: e.target.value }))}
                fullWidth
                required
              >
                {profileCrewOptions.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.firstName} {c.lastName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Training ID"
              value={form.trainingId}
              onChange={(e) => setForm((p) => ({ ...p, trainingId: e.target.value }))}
              fullWidth
              required
              disabled={!!editRow && !canEditTrainingCoreFields}
            />
            <TextField
              label="Training name"
              value={form.trainingName}
              onChange={(e) => setForm((p) => ({ ...p, trainingName: e.target.value }))}
              fullWidth
              required
              disabled={!!editRow && !canEditTrainingCoreFields}
            />
            <TextField
              select
              label="Training type"
              value={form.trainingType}
              onChange={(e) => setForm((p) => ({ ...p, trainingType: e.target.value }))}
              fullWidth
            >
              {TRAINING_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>

            <Divider />
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              2 · Dates &amp; reminders
            </Typography>
            <TextField
              label="Acquired date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.acquiredDate}
              onChange={(e) => setForm((p) => ({ ...p, acquiredDate: e.target.value }))}
              fullWidth
              required
              disabled={!!editRow && !canEditTrainingCoreFields}
            />
            <TextField
              label="Performed on date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.performedOnDate}
              onChange={(e) => setForm((p) => ({ ...p, performedOnDate: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Expiry date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.hasNoExpiry ? '' : form.expiryDate}
                onChange={(e) => setForm((p) => ({ ...p, expiryDate: e.target.value }))}
                fullWidth
                disabled={form.hasNoExpiry}
                required={!form.hasNoExpiry}
              />
              <TextField
                select
                label="Has expiry?"
                value={form.hasNoExpiry ? 'no' : 'yes'}
                onChange={(e) => {
                  const expires = e.target.value === 'yes';
                  setForm((p) => ({
                    ...p,
                    hasNoExpiry: !expires,
                    periodicityMonths: expires ? p.periodicityMonths || '12' : '',
                    expiryDate: expires ? p.expiryDate : '',
                    reminderDates: expires ? p.reminderDates : []
                  }));
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="yes">Expires</MenuItem>
                <MenuItem value="no">No expiry</MenuItem>
              </TextField>
            </Stack>
            {!form.hasNoExpiry && (
              <>
                <TextField
                  label="Periodicity (months)"
                  type="number"
                  value={form.periodicityMonths}
                  onChange={(e) => setForm((p) => ({ ...p, periodicityMonths: e.target.value }))}
                  fullWidth
                  required
                  inputProps={{ min: 1, step: 1 }}
                />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Reminder dates (required)
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <TextField
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      label="Add reminder date"
                      value={form.reminderDateInput}
                      onChange={(e) => setForm((p) => ({ ...p, reminderDateInput: e.target.value }))}
                      sx={{ flex: 1 }}
                    />
                    <Button variant="outlined" onClick={addReminderDate} sx={{ textTransform: 'none' }}>
                      Add
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {form.reminderDates.map((d) => (
                      <Chip key={d} label={d} onDelete={() => removeReminderDate(d)} size="small" />
                    ))}
                    {form.reminderDates.length === 0 && (
                      <Typography variant="body2" color="error">
                        At least one reminder date is required.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </>
            )}

            <Divider />
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              3 · Proof file &amp; notes
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Attach a training certificate or proof document (PDF / image). Optional URL if the file is hosted elsewhere.
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  variant="contained"
                  component="label"
                  color={form.file ? 'success' : 'primary'}
                  sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                >
                  {form.file ? form.file.name : editRow ? 'Replace proof file' : 'Upload proof file'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,image/*"
                    onChange={(e) => setForm((p) => ({ ...p, file: e.target.files?.[0] || null }))}
                  />
                </Button>
                <TextField
                  label="Certificate / proof URL (optional)"
                  value={form.certificateUrl}
                  onChange={(e) => setForm((p) => ({ ...p, certificateUrl: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Stack>
            </Paper>
            <TextField
              label="Remarks"
              value={form.remarks}
              onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#1B4F72', '&:hover': { bgcolor: '#154360' } }}
          >
            {editRow ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CrewTrainingUpdatesPanel;
